import React, { useCallback, useEffect, useState } from 'react';
import { Download, File, FileImage, FileSpreadsheet, FileText, Paperclip } from 'lucide-react';
import BBALoader from '../BBALoader';
import DocumentViewerModal from '../DocumentViewerModal';
import { fetchGmailAttachment } from '../../services/gmailService';

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|svg)$/i;
const SPREADSHEET_EXT = /\.(xlsx?|csv)$/i;
const PREVIEW_EXT = /\.(pdf|txt|png|jpe?g|gif|webp|bmp|svg|xlsx?|csv)$/i;
// Signature logos and spacer gifs ride along as inline images on most replies.
const MIN_INLINE_IMAGE_BYTES = 8 * 1024;
// Thumbnails are fetched automatically, so keep them to reasonably small images.
const MAX_THUMBNAIL_BYTES = 5 * 1024 * 1024;

const MIME_BY_EXT = {
  pdf: 'application/pdf',
  txt: 'text/plain',
  csv: 'text/csv',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  svg: 'image/svg+xml',
};

// Gmail often labels files application/octet-stream; the browser needs the real
// type to render a PDF or image instead of downloading it.
const resolveMimeType = (filename, mimeType) => {
  const ext = String(filename || '').split('.').pop().toLowerCase();
  if (!mimeType || mimeType === 'application/octet-stream') return MIME_BY_EXT[ext] || 'application/octet-stream';
  return mimeType;
};

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const iconFor = (filename) => {
  if (IMAGE_EXT.test(filename)) return FileImage;
  if (SPREADSHEET_EXT.test(filename)) return FileSpreadsheet;
  if (/\.(pdf|txt|docx?|rtf)$/i.test(filename)) return FileText;
  return File;
};

// Object URLs per attachment, shared across re-renders and thread re-opens.
const objectUrlCache = new Map();

const loadAttachment = async (ownerId, messageId, attachment) => {
  const key = `${ownerId}:${messageId}:${attachment.partId}`;
  if (objectUrlCache.has(key)) return objectUrlCache.get(key);
  const pending = fetchGmailAttachment(ownerId, messageId, attachment.partId).then(({ blob, filename, mimeType }) => {
    const typed = new Blob([blob], { type: resolveMimeType(filename, mimeType) });
    return { url: URL.createObjectURL(typed), filename: filename || attachment.filename };
  });
  objectUrlCache.set(key, pending);
  // A failed load shouldn't stick: drop it so the next click retries.
  pending.catch(() => objectUrlCache.delete(key));
  return pending;
};

const triggerDownload = (url, filename) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const ImageThumbnail = ({ ownerId, messageId, attachment }) => {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    loadAttachment(ownerId, messageId, attachment)
      .then((loaded) => {
        if (!cancelled) setUrl(loaded.url);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [ownerId, messageId, attachment]);

  if (!url) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <BBALoader size={22} glow={false} />
      </div>
    );
  }
  return <img src={url} alt={attachment.filename} className="w-full h-full object-cover" />;
};

/**
 * Attachments on one Gmail message (as listed by the fetchGmailMessages callable).
 * Images show thumbnails; clicking a PDF, image, text file or spreadsheet opens it
 * in the fullscreen in-app viewer, anything else (Word docs, zips…) downloads.
 */
const EmailAttachments = ({ message }) => {
  const [viewer, setViewer] = useState(null);
  const [busyPart, setBusyPart] = useState(null);
  const [error, setError] = useState('');

  const ownerId = message?.fetchedBy?.id;
  const messageId = message?.id;
  const attachments = (Array.isArray(message?.attachments) ? message.attachments : []).filter(
    (attachment) =>
      attachment?.partId &&
      !(attachment.inline && IMAGE_EXT.test(attachment.filename) && attachment.size < MIN_INLINE_IMAGE_BYTES)
  );

  const closeViewer = useCallback(() => setViewer(null), []);

  if (!attachments.length) return null;

  const handleOpen = async (attachment, { download = false } = {}) => {
    if (!ownerId || !messageId || busyPart) return;
    setError('');
    setBusyPart(attachment.partId);
    try {
      const loaded = await loadAttachment(ownerId, messageId, attachment);
      if (!download && PREVIEW_EXT.test(loaded.filename)) {
        setViewer(loaded);
      } else {
        triggerDownload(loaded.url, loaded.filename);
      }
    } catch (err) {
      setError(err?.message || 'Could not load this attachment.');
    } finally {
      setBusyPart(null);
    }
  };

  return (
    <div className="mt-3 min-w-0">
      <div className="flex items-center gap-1.5 mb-2 text-xs font-medium text-gray-500 dark:text-gray-400">
        <Paperclip className="w-3.5 h-3.5" aria-hidden />
        {attachments.length} attachment{attachments.length === 1 ? '' : 's'}
      </div>
      <div className="flex flex-wrap gap-2 min-w-0">
        {attachments.map((attachment) => {
          const Icon = iconFor(attachment.filename);
          const busy = busyPart === attachment.partId;
          const showThumb = IMAGE_EXT.test(attachment.filename) && attachment.size <= MAX_THUMBNAIL_BYTES;
          return (
            <div
              key={attachment.partId}
              className="group w-44 max-w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden"
            >
              <button
                type="button"
                onClick={() => handleOpen(attachment)}
                disabled={!!busyPart}
                title={`Open ${attachment.filename}`}
                className="w-full text-left disabled:cursor-wait"
              >
                <div className="h-24 bg-gray-100 dark:bg-gray-800 flex items-center justify-center overflow-hidden">
                  {showThumb && ownerId && messageId ? (
                    <ImageThumbnail ownerId={ownerId} messageId={messageId} attachment={attachment} />
                  ) : (
                    <Icon className="w-9 h-9 text-gray-400 dark:text-gray-500" aria-hidden />
                  )}
                </div>
              </button>
              <div className="flex items-center gap-1 px-2 py-1.5 min-w-0">
                <button
                  type="button"
                  onClick={() => handleOpen(attachment)}
                  disabled={!!busyPart}
                  className="min-w-0 flex-1 text-left disabled:cursor-wait"
                >
                  <div className="text-xs font-medium text-gray-800 dark:text-gray-100 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400">
                    {attachment.filename}
                  </div>
                  <div className="text-[11px] text-gray-400 dark:text-gray-500">{formatSize(attachment.size)}</div>
                </button>
                {busy ? (
                  <BBALoader size={18} glow={false} className="shrink-0" />
                ) : (
                  <button
                    type="button"
                    onClick={() => handleOpen(attachment, { download: true })}
                    disabled={!!busyPart}
                    title="Download"
                    aria-label={`Download ${attachment.filename}`}
                    className="shrink-0 p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-700"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>}

      <DocumentViewerModal
        isOpen={!!viewer}
        onClose={closeViewer}
        url={viewer?.url}
        title={viewer?.filename}
        fileName={viewer?.filename}
      />
    </div>
  );
};

export default EmailAttachments;
